/**
 * Pages/Panel/Empleados/Finiquito/GuiaFiniquito.js
 *
 * La guía de uso del finiquito, dentro de la propia pantalla.
 *
 * Está aquí y no sólo en el portal de manuales porque quien la necesita ya está a media captura:
 * un finiquito se hace pocas veces al año, casi siempre con prisa y con el empleado enfrente, y
 * mandar a buscar el manual en otra parte es mandar a adivinar.
 *
 * El contenido son los pasos MÁS las trampas reales del módulo — las que producen un finiquito en
 * ceros o un CFDI rechazado sin decir por qué. Ésas son las que la gente no puede deducir de la
 * pantalla, y son la razón de que esto valga la pena.
 */

import React from "react";
import { Collapse, Tag, Alert } from "antd";
import { ModalItem, ButtonItem } from "../../../../Components/Items";

const { Panel } = Collapse;

const NUM = {
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  width: 22, height: 22, borderRadius: "50%", background: "#00BFD6",
  color: "white", fontSize: 12, fontWeight: 700, marginRight: 8, flex: "0 0 auto",
};

const Paso = ({ n, titulo, children }) => (
  <div style={{ display: "flex", marginBottom: 14 }}>
    <span style={NUM}>{n}</span>
    <div style={{ flex: 1 }}>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>{titulo}</div>
      <div style={{ fontSize: 12, color: "#555", lineHeight: 1.55 }}>{children}</div>
    </div>
  </div>
);

const GuiaFiniquito = ({ visible, onClose }) => (
  <ModalItem
    title="Cómo hacer un finiquito"
    visible={visible}
    onCancel={onClose}
    paddingTop
    width={780}
    footer={null}
  >
    <Alert
      type="info"
      showIcon
      style={{ marginBottom: 16 }}
      message="El finiquito se paga dentro de la nómina del periodo que elijas"
      description={
        <span style={{ fontSize: 12 }}>
          No se crea una nómina aparte: los conceptos entran al recibo del empleado en la prenómina
          de ese periodo. Ahí lo verás en su propia tabla, <b>Finiquitos</b>, separado de la nómina
          ordinaria, y con su propia póliza contable.
        </span>
      }
    />

    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>Los pasos</div>

    <Paso n={1} titulo="Captura la fecha de baja y la causa">
      La <b>causa</b> no es sólo un dato: decide qué se paga. Un despido trae indemnización de 90
      días y prima de antigüedad; una separación voluntaria no trae indemnización, y la prima de
      antigüedad sólo se paga con 15 años o más de antigüedad (Art. 162-III LFT). Si hubo convenio,
      puedes activar y capturar esos conceptos a mano.
    </Paso>

    <Paso n={2} titulo="Elige el periodo de aplicación">
      Se ofrecen el periodo de la baja y los dos siguientes. Sólo se habilitan los que tienen su
      prenómina abierta <b>y</b> al empleado dentro de ella; si falta la línea, aparece un botón
      para agregarlo sin salir de aquí. En un periodo posterior al de la baja el sueldo y el séptimo
      día salen en cero: el empleado ya no laboró ningún día de ese periodo.
    </Paso>

    <Paso n={3} titulo="Captura la fecha de pago (opcional)">
      Es el día en que realmente le pagas la liquidación. Manda en la <b>póliza contable</b> y en la
      fecha de pago del <b>CFDI</b>. Si la dejas vacía se usa la de la prenómina. Ojo: sólo se puede
      cambiar mientras no se haya generado la póliza, o sea antes de timbrar.
    </Paso>

    <Paso n={4} titulo="Presiona Calcular y revisa los días">
      El sistema propone los días de cada concepto y te deja capturar los tuyos. Lo capturado gana
      sobre lo propuesto. Si falta algún dato del empleado, sale una alerta roja diciendo cuál y
      Aplicar queda deshabilitado — con una base faltante el finiquito se guardaría en ceros.
    </Paso>

    <Paso n={5} titulo="Ajusta los importes en la pestaña 2">
      Funciona como el sobre-recibo de la nómina: escribe sobre cualquier importe y sal del campo.
      Lo que quede en <Tag color="blue" style={{ fontSize: 10, padding: "0 4px" }}>M</Tag> azul es lo
      que capturaste y <b>gana sobre la fórmula</b> del concepto. Ahí mismo agregas conceptos que no
      estaban (último renglón de cada columna) y quitas los que no van, con la basurita.
    </Paso>

    <Paso n={6} titulo="Aplica el finiquito">
      Da de baja al empleado y mete los conceptos a su recibo. <b>No timbra nada todavía.</b> Y sigue
      siendo editable: puedes seguir ajustando días e importes hasta que lo timbres.
    </Paso>

    <Paso n={7} titulo="Timbra">
      Puedes timbrarlo aquí mismo —sólo ese recibo— o dejarlo y que se timbre junto con el resto de
      la prenómina. Antes de mandar al SAT se validan los datos del empleado y, si falta algo, se
      dice qué.
    </Paso>

    <Paso n={8} titulo="Entrega los documentos">
      <b>Recibo (PDF / XML)</b> es el comprobante fiscal, y desde ahí también se manda por correo.
      La <b>Carta Finiquito</b> es la que firma el empleado. La <b>Hoja de Trabajo</b> es el respaldo
      interno: trae las bases del cálculo y lo propuesto contra lo capturado, concepto por concepto.
    </Paso>

    <div style={{ fontWeight: 600, fontSize: 13, margin: "18px 0 8px" }}>
      Lo que conviene saber antes de que pase
    </div>

    <Collapse ghost>
      <Panel header="¿Por qué salieron DOS recibos y dos UUID?" key="1">
        <div style={{ fontSize: 12, color: "#555", lineHeight: 1.6 }}>
          Porque el SAT lo exige. Desde 2020, los pagos por indemnización o separación (claves 022,
          023 y 025) deben identificarse con el <b>régimen 13</b>, y un comprobante no puede tener
          dos regímenes a la vez. Así que cuando el finiquito trae esos pagos se emiten dos CFDI: uno
          con los conceptos ordinarios y otro con los de separación y su ISR. Es un solo recibo y un
          solo pago; son dos comprobantes. Los dos se cancelan juntos, y en el visor de recibos se
          elige cuál ver.
        </div>
      </Panel>

      <Panel header="Un concepto tiene bien los días pero paga 0.00" key="2">
        <div style={{ fontSize: 12, color: "#555", lineHeight: 1.6 }}>
          Es una multiplicación por cero: alguna de las bases del concepto está vacía y no hay error
          a la vista. Las tres más comunes:
          <ul style={{ paddingLeft: 18, margin: "6px 0 0" }}>
            <li>
              <b>Prima de antigüedad</b>: se topa a 2 salarios mínimos, así que necesita la tabla
              SALARIOS_MINIMOS capturada con un renglón que cubra la fecha de baja y con la columna
              de la zona de tu empresa.
            </li>
            <li>
              <b>Aguinaldo, vacaciones y prima vacacional</b>: se calculan sobre el salario base de
              cotización. Sin SBC en la ficha del empleado pagan cero, y el salario diario no lo
              sustituye.
            </li>
            <li>
              <b>Indemnización y separación única</b>: usan el salario base de liquidación de la
              ficha, o el diario si no hay.
            </li>
          </ul>
          La alerta roja de la pantalla dice cuál falta. Si aun así no sale, en la consola del
          servidor queda el detalle concepto por concepto.
        </div>
      </Panel>

      <Panel header="¿Cuánto queda exento de ISR?" key="3">
        <div style={{ fontSize: 12, color: "#555", lineHeight: 1.6 }}>
          90 UMA por cada año de servicio (Art. 93-XIII LISR), y es una <b>bolsa compartida</b> entre
          indemnización, separación única y prima de antigüedad: no son 90 UMA por concepto. Lo que
          exceda va gravado, y el ISR se calcula por la tasa efectiva del último sueldo mensual
          ordinario (Art. 96, último párrafo). Los años que se usan para la exención se capturan en
          la pestaña 1 y por omisión son los de antigüedad.
        </div>
      </Panel>

      <Panel header="Me equivoqué. ¿Cómo lo corrijo?" key="4">
        <div style={{ fontSize: 12, color: "#555", lineHeight: 1.6 }}>
          Depende de qué tan lejos hayas llegado:
          <ul style={{ paddingLeft: 18, margin: "6px 0 0" }}>
            <li><b>Aún no aplicas</b>: cambia lo que sea y vuelve a calcular. No se ha escrito nada.</li>
            <li>
              <b>Ya aplicaste, no has timbrado</b>: ajusta días e importes directamente; cada cambio
              se guarda. Para cambiar la fecha de baja, la causa o el periodo, usa Deshacer.
            </li>
            <li>
              <b>Ya timbraste</b>: usa <b>Cancelar timbrado</b>. Cancela los CFDI ante el SAT
              (los dos, si se partió), revierte acumulados y créditos, y cancela su póliza. Después
              queda editable otra vez y se puede volver a timbrar.
            </li>
            <li>
              <b>Empezar de cero</b>: <b>Deshacer</b> quita los conceptos del recibo, borra los
              movimientos y reactiva al empleado.
            </li>
            <li>
              <b>No fue un error: volvió a trabajar</b>: usa <b>Reingresar</b>, no Deshacer. Deshacer
              borra la baja como si nunca se hubiera ido; Reingresar reactiva al mismo empleado con
              una fecha de reingreso y deja la baja y el finiquito guardados como historia. Pide que
              los recibos de la relación anterior ya estén timbrados.
            </li>
          </ul>
        </div>
      </Panel>

      <Panel header="¿Y la contabilidad?" key="5">
        <div style={{ fontSize: 12, color: "#555", lineHeight: 1.6 }}>
          Cada finiquito genera <b>su propia póliza</b>, aparte de la de la nómina del periodo, y se
          registra con la fecha de pago que capturaste. Se crea al timbrar. Si cancelas, esa póliza
          se cancela completa; al volver a timbrar se genera una nueva. Si algún concepto del
          finiquito no tiene cuentas contables asignadas, la pantalla lo avisa: la póliza saldría
          incompleta, aunque el CFDI se timbre igual.
        </div>
      </Panel>
    </Collapse>

    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
      <ButtonItem type="primary" onClick={onClose}>Entendido</ButtonItem>
    </div>
  </ModalItem>
);

export default GuiaFiniquito;
